/**
 * Description Parser - React ES Module
 *
 * Contains all parsing functions for deal/special descriptions.
 * The pure logic is duplicated in lib/descriptionParser.js (CommonJS) for Jest tests.
 * Any changes to parsing logic MUST be mirrored in both files.
 *
 * Usage in React components:
 *   import { parseItemsFromDescription, formatDescription } from '../utils/descriptionParser';
 */

import React from 'react';

// ============================================================================
// KEYWORD LISTS
// ============================================================================

export const FOOD_KEYWORDS = [
  // General
  'food', 'appetizer', 'appetizers', 'starter', 'starters', 'entree', 'entrees',
  'shareables', 'bites', 'bar bites', 'snacks', 'snack', 'small plates',
  'tray', 'platter', 'board', 'spread', 'relish',
  // American/Pub
  'wings', 'burger', 'burgers', 'fries', 'pizza', 'sandwich', 'salad', 'ribs',
  'sliders', 'slider', 'nachos', 'pretzel', 'pretzels', 'bacon', 'hot dog',
  'tenders', 'chicken tenders', 'popcorn', 'nuts', 'curds', 'cheese curds',
  'poutine', 'mozzarella sticks', 'onion rings', 'corn dog', 'po boy',
  'mac and cheese', 'mac n cheese', 'pulled pork', 'brisket', 'coleslaw',
  'hushpuppies', 'hush puppies', 'hush puppy',
  'tots', 'tater kegs', 'potato skins', 'skins',
  'meatballs', 'meatball',
  'pickle', 'pickles', 'fried pickle', 'fried pickles',
  'pork rinds', 'cracklins',
  'deviled eggs',
  'cornbread', 'corn bread',
  'grits', 'gravy',
  'loaded fries', 'loaded tots',
  'fried green tomatoes',
  'fish fry', 'fish sandwich',
  'wrap', 'wraps', 'flatbread sandwich',
  'bowl', 'bowls', 'grain bowl',
  'skewers', 'kebab skewers',
  'tray', 'sampler', 'combo', 'platter', 'basket',
  // Protein
  'shrimp', 'prawn', 'prawns', 'filet', 'beef', 'lamb', 'chicken', 'salmon',
  'steak', 'meat', 'fish', 'pork', 'turkey', 'duck', 'veal', 'bison',
  'lobster', 'crab', 'oyster', 'oysters', 'mussels', 'clams', 'scallops',
  'calamari', 'squid', 'octopus', 'sausage', 'bratwurst', 'kielbasa',
  // Cheese/Dairy
  'cheese', 'goat cheese', 'brie', 'cheddar', 'mozzarella', 'parmesan',
  'gorgonzola', 'gruyere', 'provolone', 'manchego', 'burrata',
  // Italian
  'bruschetta', 'flatbread', 'pizzetta', 'antipasto', 'crostini',
  'charcuterie', 'arancini', 'caprese', 'focaccia', 'gnocchi', 'risotto',
  'ravioli', 'lasagna', 'penne', 'spaghetti', 'fettuccine', 'linguine',
  'carbonara', 'bolognese', 'alfredo', 'marinara', 'pesto', 'prosciutto',
  'pancetta', 'mortadella', 'salami', 'sopressata',
  // Mexican/Latin
  'tacos', 'taco', 'quesadilla', 'quesadillas', 'queso', 'churro', 'churros',
  'elote', 'tamale', 'tamales', 'ceviche', 'arepa', 'arepas', 'pupusa',
  'torta', 'flautas', 'birria', 'carnitas', 'empanada', 'empanadas',
  'botana', 'taquitos', 'enchilada', 'enchiladas', 'burrito', 'burritos',
  'chimichanga', 'tostada', 'tostadas', 'gordita', 'sope', 'pozole',
  'mole', 'al pastor', 'barbacoa', 'chorizo', 'chicharron',
  // Asian
  'sushi', 'dumpling', 'dumplings', 'egg roll', 'spring roll', 'wonton',
  'gyoza', 'edamame', 'tempura', 'teriyaki', 'ramen', 'pho', 'pad thai',
  'satay', 'curry', 'tikka', 'masala', 'naan', 'samosa', 'pakora',
  'dim sum', 'bao', 'banh mi', 'bibimbap', 'bulgogi', 'kimchi',
  'yakitori', 'katsu', 'udon', 'soba', 'miso', 'tofu', 'bok choy',
  // Mediterranean/Middle Eastern
  'hummus', 'falafel', 'pita', 'shawarma', 'kebab', 'kebabs', 'tzatziki',
  'dolma', 'tabbouleh', 'baba ghanoush', 'fattoush', 'labneh', 'kibbeh',
  'spanakopita', 'gyro', 'souvlaki', 'moussaka',
  // Seafood
  'shrimp cocktail', 'prawn cocktail',
  'tuna', 'ahi', 'tartare', 'crudo', 'poke', 'ceviche', 'fish tacos',
  'fish and chips', 'fish fry', 'crawfish', 'catfish', 'cod', 'halibut',
  'walleye', 'perch', 'tilapia', 'grouper', 'mahi', 'swordfish',
  // Vegetables/Sides
  'potato', 'potatoes', 'tater', 'tater tots', 'wedges', 'egg', 'eggs', 'soup', 'chili',
  'guac', 'guacamole', 'salsa', 'salsas', 'avocado', 'corn', 'rice',
  'beans', 'onion', 'onions', 'tomato', 'cilantro', 'peppers',
  'artichokes', 'mushroom', 'mushrooms', 'asparagus', 'brussels sprouts',
  'jalapeño', 'poblano', 'chipotle', 'plantain', 'plantains',
  'sweet potato', 'couscous', 'quinoa', 'polenta',
  // Cooking methods/descriptors
  'seared', 'roasted', 'grilled', 'fried', 'smoked', 'braised',
  'blackened', 'battered', 'breaded', 'glazed', 'stuffed', 'wrapped',
  // Sauces/Condiments
  'aioli', 'brava', 'dip', 'chips', 'olives', 'pintxos', 'skewer',
  // European
  'schnitzel', 'pierogi', 'pierogies', 'crepe', 'crepes', 'quiche',
  'croque monsieur', 'escargot', 'fondue', 'ratatouille',
  // Dessert (sometimes on HH menus)
  'brownie', 'cookie', 'cookies', 'cheesecake', 'tiramisu', 'gelato',
  'sundae', 'pie', 'cobbler', 'bread pudding'
];

export const DRINK_KEYWORDS = [
  // General
  'drink', 'drinks', 'beverage', 'cocktail', 'cocktails', 'mocktail',
  'well', 'spirits', 'booze', 'pour', 'shot', 'shots', 'shooter',
  'on tap', 'on draft', 'happy hour drink', 'house pour',
  // Beer
  'beer', 'beers', 'draft', 'drafts', 'pint', 'pints', 'pitcher',
  'ale', 'lager', 'ipa', 'stout', 'porter', 'pilsner', 'hefeweizen',
  'wheat beer', 'amber', 'pale ale', 'brown ale', 'kolsch', 'saison',
  'sour beer', 'gose', 'witbier', 'dunkel', 'bock', 'doppelbock',
  'shandy', 'radler', 'cider', 'hard cider', 'craft beer',
  'domestic', 'import', 'microbrew', 'tallboy', 'tall boy', 'can', 'bottle',
  // Wine
  'wine', 'wines', 'glass', 'red wine', 'white wine', 'rose', 'rosé',
  'champagne', 'prosecco', 'sparkling', 'bubbly', 'bubbles',
  'merlot', 'chardonnay', 'cabernet', 'pinot', 'sauvignon', 'riesling',
  'moscato', 'malbec', 'tempranillo', 'shiraz', 'syrah', 'zinfandel',
  'sangiovese', 'nebbiolo', 'grenache', 'viognier', 'gewurztraminer',
  'chenin blanc', 'gruner veltliner', 'albariño', 'verdejo', 'torrontes',
  'cava', 'cremant', 'brut', 'blanc de blancs', 'sangria',
  // Wine styles & regions (appear in scraped wine list descriptions)
  'super tuscan', 'chianti', 'barolo', 'brunello', 'valpolicella',
  'beaujolais', 'chablis', 'sancerre', 'bordeaux', 'burgundy',
  'rioja', 'rueda', 'montepulciano',
  // Classic cocktails
  'margarita', 'margaritas', 'martini', 'martinis', 'manhattan',
  'old fashioned', 'negroni', 'daiquiri', 'mojito', 'cosmopolitan', 'cosmo',
  'moscow mule', 'mule', 'gimlet', 'sidecar', 'tom collins', 'highball',
  'whiskey sour', 'sour', 'paloma', 'caipirinha', 'mai tai',
  'pina colada', 'long island', 'bloody mary', 'mimosa', 'bellini',
  'spritz', 'spritzes', 'spritzs', 'aperol spritz', 'hugo',
  'julep', 'mint julep', 'sazerac', 'boulevardier', 'penicillin',
  'last word', 'aviation', 'french 75', 'corpse reviver', 'bramble',
  'dark and stormy', 'painkiller', 'zombie', 'hurricane', 'tiki',
  // Popular named cocktails
  'smoke & stone', 'smoke and stone',
  'lemon drop', 'sex on the beach', 'tequila sunrise', 'amaretto sour',
  'irish coffee', 'espresso martini', 'white russian', 'black russian',
  'mudslide', 'grasshopper', 'blue lagoon', 'singapore sling',
  'tom and jerry', 'hot toddy', 'toddy', 'grog', 'punch',
  'sangrita', 'michelada', 'chelada', 'agua fresca',
  // Spirits
  'bourbon', 'whiskey', 'whisky', 'rye', 'scotch', 'vodka', 'tequila',
  'mezcal', 'rum', 'gin', 'brandy', 'cognac', 'armagnac', 'grappa',
  'absinthe', 'chartreuse', 'vermouth', 'amaro', 'fernet',
  'amaretto', 'kahlua', 'baileys', 'grand marnier', 'cointreau',
  'triple sec', 'curacao', 'campari', 'aperol', 'limoncello',
  'sambuca', 'ouzo', 'pastis', 'pisco', 'cachaca', 'soju', 'sake',
  'baijiu', 'aquavit', 'schnapps', 'kirsch', 'calvados',
  // Brands commonly on HH menus
  'fireball', "tito's", 'jameson', 'jack daniels', 'crown royal',
  'hennessy', 'patron', 'don julio', 'casamigos', 'grey goose',
  'ketel one', 'absolut', 'smirnoff', 'bacardi', 'captain morgan',
  'malibu', 'hendricks', 'tanqueray', 'beefeater', 'bombay',
  'maker', 'woodford', 'bulleit', 'buffalo trace', 'wild turkey',
  'knob creek', 'four roses', 'evan williams', 'elijah craig',
  'modelo', 'corona', 'budweiser', 'bud light', 'miller lite',
  'coors', 'heineken', 'stella', 'guinness', 'blue moon',
  'white claw', 'truly', 'hard seltzer', 'seltzer',
  'krombacher', 'high noon', 'nutrl', 'pabst', 'pbr',
  'miller high life', 'sam adams',
  // Non-alcoholic
  'mocktail', 'virgin', 'non-alcoholic', 'na beer', 'kombucha'
];

// ============================================================================
// CUSTOM KEYWORD OVERRIDE (set at runtime from Airtable Settings)
// ============================================================================

let _customFoodKeywords = [];
let _customDrinkKeywords = [];

export const setCustomKeywords = (food = [], drink = []) => {
  _customFoodKeywords = food.map(k => k.toLowerCase().trim()).filter(Boolean);
  _customDrinkKeywords = drink.map(k => k.toLowerCase().trim()).filter(Boolean);
};

export const getEffectiveFoodKeywords = () =>
  _customFoodKeywords.length ? [...FOOD_KEYWORDS, ..._customFoodKeywords.filter(k => !FOOD_KEYWORDS.includes(k))] : FOOD_KEYWORDS;

export const getEffectiveDrinkKeywords = () =>
  _customDrinkKeywords.length ? [...DRINK_KEYWORDS, ..._customDrinkKeywords.filter(k => !DRINK_KEYWORDS.includes(k))] : DRINK_KEYWORDS;

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

export const countKeywords = (text, keywords) => {
  if (!text) return 0;
  const lower = text.toLowerCase();
  return keywords.filter(kw => {
    // Multi-word keywords or keywords with apostrophes: use simple includes
    if (kw.includes(' ') || kw.includes("'")) return lower.includes(kw);
    // Short keywords (≤4 chars) need word-boundary matching to avoid false positives
    // (e.g., "can" in "Mexican", "ale" in "tamale", "gin" in "ginger")
    if (kw.length <= 4) {
      const re = new RegExp(`\\b${kw}\\b`);
      return re.test(lower);
    }
    // Longer keywords (5+ chars): use includes() — safe from false positives
    // and allows compound word matching (e.g., "burger" in "cheeseburger")
    return lower.includes(kw);
  }).length;
};

export const categorizeDeal = (dealName, description) => {
  const FOOD_KEYWORDS = getEffectiveFoodKeywords();
  const DRINK_KEYWORDS = getEffectiveDrinkKeywords();
  // Prioritize the deal NAME for categorization — the name tells you WHAT it is
  // (e.g., "Altiro Old Fashioned" = drink, "Ala Papa Brava" = food).
  // Descriptions often list ingredients that cross categories
  // (e.g., a cocktail description mentioning cilantro, lime, cherry).
  const nameFoodCount = countKeywords((dealName || '').toLowerCase(), FOOD_KEYWORDS);
  const nameDrinkCount = countKeywords((dealName || '').toLowerCase(), DRINK_KEYWORDS);

  // If the name alone is decisive, use it (avoids ingredient confusion)
  if (nameDrinkCount > nameFoodCount && nameFoodCount === 0) return 'Drinks';
  if (nameFoodCount > nameDrinkCount && nameDrinkCount === 0) return 'Food';
  // Name has both but food wins (e.g. "Jumbo Shrimp Cocktail": shrimp×2 > cocktail×1)
  if (nameFoodCount > nameDrinkCount) return 'Food';
  if (nameDrinkCount > nameFoodCount) return 'Drinks';

  // Name is ambiguous or has both — check combined text
  const combined = `${dealName || ''} ${description || ''}`.toLowerCase();
  const foodCount = countKeywords(combined, FOOD_KEYWORDS);
  const drinkCount = countKeywords(combined, DRINK_KEYWORDS);

  // If both food and drink keywords present, decide if it's truly mixed or
  // just a cocktail with food-sounding ingredients.
  if (foodCount > 0 && drinkCount > 0) {
    // Count how many distinct priced items are in the description.
    // Multi-item deals (like "Beer $4, Fries $5") are genuinely mixed → Food & Drinks.
    // Single-item deals with a spirit name are cocktails → Drinks.
    const priceCount = ((description || '').match(/\$\d+/g) || []).length;
    if (priceCount <= 1) {
      // Single-item deal — check for strong drink signals (spirit/cocktail names)
      // that indicate it's a cocktail, not food with a drink ingredient.
      const strongDrinkSignals = [
        'vodka', 'tequila', 'mezcal', 'whiskey', 'whisky', 'bourbon', 'rum',
        'gin', 'brandy', 'cognac', 'scotch', 'absinthe', 'vermouth',
        'cocktail', 'martini', 'margarita', 'daiquiri', 'negroni',
        'old fashioned', 'manhattan', 'mojito', 'sangria', 'mimosa',
      ];
      const hasStrongDrink = strongDrinkSignals.some(s => combined.includes(s));
      if (hasStrongDrink) return 'Drinks';
    }
    return 'Food & Drinks';
  }
  if (foodCount > 0) return 'Food';
  if (drinkCount > 0) return 'Drinks';
  return 'Other';
};

export const categorizeItem = (itemText) => {
  const lower = (itemText || '').toLowerCase();
  const foodCount = countKeywords(lower, getEffectiveFoodKeywords());
  const drinkCount = countKeywords(lower, getEffectiveDrinkKeywords());

  if (foodCount > drinkCount) return 'Food';
  if (drinkCount > foodCount) return 'Drinks';
  return 'Other';
};

// ============================================================================
// ITEM PARSING
// ============================================================================

export const cleanItemName = (item) => {
  if (!item) return item;
  const fillerPhrases = [
    /^and\s+food\s+items?\s+like\s+/i,
    /^and\s+drinks?\s+like\s+/i,
    /^food\s+items?\s+like\s+/i,
    /^drinks?\s+like\s+/i,
    /^items?\s+like\s+/i,
    /^including\s+/i,
    /^such\s+as\s+/i,
    /^with\s+/i,
    /^like\s+/i,
    /^and\s+/i,
    /^or\s+/i,
    /^features?\s+/i,
  ];
  // Strip trailing section headers that Claude sometimes appends (e.g. "Item. Drink specials:")
  const trailingSectionHeaders = /[\.\,\s]+(?:drink\s+specials?|food\s+specials?|drinks?|food|specials?)\s*:\s*$/i;

  let cleaned = item.replace(trailingSectionHeaders, '');
  for (const phrase of fillerPhrases) {
    cleaned = cleaned.replace(phrase, '');
  }
  return cleaned.trim();
};

export const isInvalidItem = (item) => {
  if (!item || item.length < 3) return true;
  if (/serves?\s*\d/i.test(item)) return true;
  if (/^\(/.test(item)) return true;
  if (/^for\s/i.test(item)) return true;
  return false;
};

// Split on commas but not commas inside parentheses
const splitOnCommas = (text) => {
  const segments = [];
  let depth = 0;
  let current = '';
  for (const ch of text) {
    if (ch === '(') depth++;
    else if (ch === ')') depth--;
    if (ch === ',' && depth === 0) {
      segments.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  if (current) segments.push(current);
  return segments;
};

export const parseItemsFromDescription = (description) => {
  if (!description) return [];
  // Trim leading/trailing whitespace so Airtable's trailing newlines don't corrupt the
  // newline-based format detection below (Airtable appends \n to saved text fields).
  description = description.trim();
  if (!description) return [];

  // ── Newline-based structured format (written by the editor when items have descriptions)
  // Format: "Item Name | Price\nOptional description\nNext Item | Price\nOptional description"
  // Also handles legacy format: "Item Name $Price\nOptional description" (backward compat).
  // A line is an "item line" if it contains " | " separator OR ends with $X price token.
  if (description.includes('\n') || description.includes('|')) {
    const lines = description.split('\n').map(l => l.trim());
    const itemLineRe = /^.+\s*\|\s*.+$|^.{1,80}\$\d+(?:\.\d{1,2})?$/;
    const itemLines = lines.filter(l => itemLineRe.test(l));
    const CAT_TAG_RE = /\s*\[(food|drinks|other)\]\s*$/i;
    const parseTag = (s) => {
      const m = s.match(CAT_TAG_RE);
      if (!m) return { clean: s, override: '' };
      const word = m[1].toLowerCase();
      const override = word === 'food' ? 'Food' : word === 'drinks' ? 'Drinks' : 'Other';
      return { clean: s.replace(CAT_TAG_RE, '').trim(), override };
    };
    if (itemLines.length > 0) {
      const result = [];
      let current = null;
      for (const line of lines) {
        if (!line) continue;
        if (itemLineRe.test(line)) {
          if (current) result.push(current);
          const pipeMatch = line.match(/^(.*?)\s*\|\s*(.+)$/);
          const dollarMatch = !pipeMatch && line.match(/^(.*?)\s+\$(\d+(?:\.\d{1,2})?)$/);
          if (pipeMatch) {
            const { clean: cleanPrice, override } = parseTag(pipeMatch[2].trim());
            current = {
              item: pipeMatch[1].trim(),
              price: cleanPrice,
              description: '',
              category: '',
              categoryOverride: override,
            };
          } else if (dollarMatch) {
            current = {
              item: dollarMatch[1].trim(),
              price: `$${dollarMatch[2]}`,
              description: '',
              category: '',
              categoryOverride: '',
            };
          } else {
            current = { item: line, price: '', description: '', category: '', categoryOverride: '' };
          }
        } else if (current) {
          current.description = current.description
            ? `${current.description} ${line}`
            : line;
        }
      }
      if (current) result.push(current);
      if (result.length > 0) {
        return result.map(r => ({ ...r, category: r.categoryOverride || categorizeItem(r.item) }));
      }
    }
  }

  // Strip "under $X" / "over $X" so the qualifier doesn't get parsed as a price
  let normalized = description.replace(/\b(?:under|over|below|above)\s+\$(\d+(?:\.\d{1,2})?)/gi, (_, n) => `under ${n}`);
  // Normalize: add space before $ when preceded by non-whitespace, but NOT inside parens
  // (preserves modifier text like "(+$1 flavored)") and NOT directly after '('.
  // e.g. "Walleye)$10" → "Walleye) $10" but keep "($12)" and "(+$1 note)" intact.
  normalized = normalized.replace(/\n/g, ' ').replace(/([^\s(])\$/g, (match, prev, offset, full) => {
    // Include the prev char in the slice so a closing ')' is counted before we decide
    const before = full.slice(0, offset + 1);
    const opens = (before.match(/\(/g) || []).length;
    const closes = (before.match(/\)/g) || []).length;
    if (opens > closes) return match; // inside unclosed parens — leave alone
    return prev + ' $';
  });
  const items = [];
  let match;

  // Single-item trailing price: catches full ingredient descriptions like
  // "Fresh muddled lime, fresh OJ, and 100% blue agave nectar - $10"
  // Must fire BEFORE other patterns which split on commas and truncate ingredient lists.
  // Guard: text before dash has no $ (rules out multi-item descriptions like "$5 beers, $3 tacos - $8 total")
  const trailingPriceSingle = normalized.match(/^([^$]+)[-–—]\s*\$(\d+(?:\.\d{1,2})?)\s*$/);
  if (trailingPriceSingle && !trailingPriceSingle[1].includes('$')) {
    const itemText = trailingPriceSingle[1].trim().replace(/[-–—]\s*$/, '').trim();
    if (itemText.length > 3) {
      return [{
        price: '$' + trailingPriceSingle[2],
        item: cleanItemName(itemText),
        category: categorizeItem(itemText)
      }];
    }
  }

  // Pattern P (Percent-off / discount tokens): handles non-$-anchored discount language
  // such as "25% off Industry Night", "Burger 50% off", "Half off appetizers", "BOGO drafts".
  // Extracts these segments BEFORE the $-anchored patterns run, stashes them, and strips
  // them from `normalized` so downstream patterns don't re-parse the leftover text.
  // The stashed items are appended to the final result at the end (preserving rough order).
  const percentSegmentRegex = /^(?:(\d+)%\s*off\s+(.+)|(.+?)\s+(\d+)%\s*off|(?:1\/2|half)\s*off\s+(.+)|(.+?)\s+(?:1\/2|half)\s*off|bogo\s+(.+))$/i;
  const stashedPercentItems = [];
  {
    const percentSegments = splitOnCommas(normalized);
    const percentRemaining = [];
    for (const seg of percentSegments) {
      const trimmed = seg.trim();
      if (!trimmed) continue;
      if (/\$/.test(trimmed)) { percentRemaining.push(seg); continue; }
      const pm = trimmed.match(percentSegmentRegex);
      if (!pm) { percentRemaining.push(seg); continue; }
      let priceLabel = '';
      let itemText = '';
      if (pm[1] && pm[2]) { priceLabel = pm[1] + '% OFF'; itemText = pm[2]; }
      else if (pm[3] && pm[4]) { priceLabel = pm[4] + '% OFF'; itemText = pm[3]; }
      else if (pm[5]) { priceLabel = '1/2 OFF'; itemText = pm[5]; }
      else if (pm[6]) { priceLabel = '1/2 OFF'; itemText = pm[6]; }
      else if (pm[7]) { priceLabel = 'BOGO'; itemText = pm[7]; }
      const cleaned = cleanItemName(itemText.trim());
      if (cleaned && !isInvalidItem(cleaned)) {
        stashedPercentItems.push({ price: priceLabel, item: cleaned, category: categorizeItem(cleaned) });
      } else {
        percentRemaining.push(seg);
      }
    }
    normalized = percentRemaining.join(', ');
  }
  // Helper: append stashed percent items to a result array (deduped by item name).
  const appendStashed = (arr) => {
    const seen = new Set(arr.map(i => (i.item || '').toLowerCase()));
    for (const p of stashedPercentItems) {
      if (!seen.has((p.item || '').toLowerCase())) arr.push(p);
    }
    return arr;
  };

  // Pattern 0.5: "Label $X: item1, item2, item3" — a labeled list where $X applies to the group
  // e.g. "Pub Bites Under $10: Sliders, Pretzel Bites, Cod Nuggets, Curry Chips"
  // Boundary anchors include 2+ whitespace so the label isn't absorbed into a preceding $X chunk
  // (real-world case: "…Well Drinks  Pub Bites Under $10: …"). Extracted matches are stripped
  // from `normalized` so Patterns 0/1-4 don't re-parse them incorrectly.
  const labelListRegex = /(?:^|,\s*|\s+and\s+|\s{2,})([A-Za-z][^,$:]*?)\s*\$(\d+(?:\.\d{1,2})?)\s*:\s*([^$]+?)(?=\s*(?:[.]|\$|$))/gi;
  let lm;
  while ((lm = labelListRegex.exec(normalized)) !== null) {
    const label = cleanItemName(lm[1].trim());
    const price = '$' + lm[2];
    const listText = lm[3].trim().replace(/[,.\s]+$/, '');
    const children = listText
      .split(/,\s*/)
      .map(s => cleanItemName(s.trim()))
      .filter(s => s && !isInvalidItem(s));
    if (!isInvalidItem(label) && children.length > 0) {
      items.push({
        price,
        item: label,
        category: categorizeItem(label + ' ' + children.join(' ')),
        children
      });
      // Strip this chunk from normalized so downstream patterns don't re-parse it.
      // Replace with ", " to preserve segment boundaries.
      normalized = normalized.replace(lm[0], ', ');
      // Reset lastIndex since we mutated the string
      labelListRegex.lastIndex = 0;
    }
  }

  // Pattern 0: Name ($Price - Description) format, e.g. Claude-extracted descriptions like
  // "Features St Charles Handshake ($7 - Draft of Panhead), Sidekick (Extra 4oz pour), ..."
  // Activates only when 2+ items have $price inside parentheses
  const parenPriceCount = (normalized.match(/[A-Za-z][^,().]*?\s*\(\$\d+(?:\.\d{1,2})?\s*[-–—]/gi) || []).length;
  if (parenPriceCount >= 2) {
    const parenItemPattern = /([A-Za-z][^,().]*?)\s*\(([^)]+)\)/gi;
    while ((match = parenItemPattern.exec(normalized)) !== null) {
      const rawName = cleanItemName(match[1].trim());
      const parenContent = match[2].trim();
      if (isInvalidItem(rawName)) continue;
      const priceInParen = parenContent.match(/^\$(\d+(?:\.\d{1,2})?)\s*[-–—]\s*/);
      const price = priceInParen ? '$' + priceInParen[1] : 'Free*';
      const desc = priceInParen ? parenContent.replace(/^\$\d+(?:\.\d{1,2})?\s*[-–—]\s*/, '') : parenContent;
      items.push({
        price,
        item: rawName + ' (' + desc + ')',
        category: categorizeItem(rawName + ' ' + desc)
      });
    }
    if (items.length > 0) return appendStashed(items);
  }

  // Mask $ inside non-pure-price parens so Patterns 1–4 don't split on modifier
  // prices like "(+$1 flavored)" — those should stay inside the item name.
  // Pure-price parens like "($12)" are preserved unchanged for Pattern 3.
  const DOLLAR_PH = '\uE000';
  const COMMA_PH  = '\uE001';
  normalized = normalized.replace(/\(([^)]*)\)/g, (full, inner) => {
    let result = inner;
    if (/\$/.test(inner) && !/^\s*\$\d+(?:\.\d{1,2})?\s*$/.test(inner)) {
      result = result.replace(/\$/g, DOLLAR_PH);
    }
    result = result.replace(/,/g, COMMA_PH);
    return '(' + result + ')';
  });
  const finalize = (arr) => arr.map(it => ({
    ...it,
    item: (it.item || '').replace(/\uE000/g, '$').replace(/\uE001/g, ','),
    ...(it.children ? { children: it.children.map(s => (s || '').replace(/\uE000/g, '$').replace(/\uE001/g, ',')) } : {})
  }));

  // Pattern 1: $X item
  const pattern1 = /\$(\d+(?:\.\d{1,2})?)\s+([^,$]+?)(?=,|\s+\$|\s+and\s+|$)/gi;
  while ((match = pattern1.exec(normalized)) !== null) {
    const item = cleanItemName(match[2].trim());
    // "$X off ..." is a discount description, not a priced item — keep full text as-is
    if (/^off\b/i.test(match[2].trim())) {
      items.push({ price: '', item: match[0].trim(), category: categorizeItem(match[0]) });
      continue;
    }
    if (!isInvalidItem(item)) {
      items.push({ price: '$' + match[1], item: item, category: categorizeItem(item) });
    }
  }
  if (items.length > 0) {
    // Check for additional segments (priced and unpriced) that were skipped by
    // Pattern 1's $X-prefix-only matching: e.g., "Half-Price Burgers" (no $),
    // or "fish and/or shrimp $16" (item-suffix price, Pattern 4 style).
    const segments = normalized.split(/,\s*|\s+and\s+/i);
    const dedup = new Set(items.map(i => (i.item || '').toLowerCase()));
    for (const seg of segments) {
      const trimmed = seg.trim();
      if (!trimmed) continue;
      if (/\$/.test(trimmed)) {
        // Segment contains a $ — try item-suffix form (e.g. "smash burgers $5").
        // Only match if the $ is at the END of the segment to avoid re-matching
        // a Pattern 1 capture that already consumed it.
        const trailingMatch = trimmed.match(/^([A-Za-z][^$]*?)\s+\$(\d+(?:\.\d{1,2})?)\s*$/);
        if (trailingMatch) {
          const item = cleanItemName(trailingMatch[1].trim());
          if (!isInvalidItem(item) && !dedup.has(item.toLowerCase())) {
            items.push({ price: '$' + trailingMatch[2], item, category: categorizeItem(item) });
            dedup.add(item.toLowerCase());
          }
        }
        continue;
      }
      const cleaned = cleanItemName(trimmed);
      if (!isInvalidItem(cleaned) && !dedup.has(cleaned.toLowerCase())) {
        items.push({ price: '', item: cleaned, category: categorizeItem(cleaned) });
        dedup.add(cleaned.toLowerCase());
      }
    }
    return appendStashed(finalize(items));
  }

  // Pattern 2: item for $X
  const pattern2 = /([^,$]+?)\s+for\s+\$(\d+(?:\.\d{1,2})?)/gi;
  while ((match = pattern2.exec(normalized)) !== null) {
    const item = cleanItemName(match[1].trim());
    if (!isInvalidItem(item) && !item.match(/^\d/)) {
      items.push({ price: '$' + match[2], item: item, category: categorizeItem(item) });
    }
  }
  if (items.length > 0) return appendStashed(finalize(items));

  // Pattern 3: item ($X)
  const pattern3 = /([^,$()]+?)\s*\(\$(\d+(?:\.\d{1,2})?)\)/gi;
  while ((match = pattern3.exec(normalized)) !== null) {
    const item = cleanItemName(match[1].trim());
    if (!isInvalidItem(item)) {
      items.push({ price: '$' + match[2], item: item, category: categorizeItem(item) });
    }
  }
  if (items.length > 0) return appendStashed(finalize(items));

  // Pattern 4: item $X — allow items starting with digits (e.g. "6pcs", "16 oz")
  const pattern4 = /([A-Za-z0-9][^$,]+?)\s+\$(\d+(?:\.\d{1,2})?)/gi;
  while ((match = pattern4.exec(normalized)) !== null) {
    const item = cleanItemName(match[1].trim());
    if (!isInvalidItem(item) && item.length >= 3 && !item.match(/for$/i)) {
      items.push({ price: '$' + match[2], item: item, category: categorizeItem(item) });
    }
  }

  return appendStashed(finalize(items));
};

// ============================================================================
// DEAL GROUPING
// ============================================================================

export const groupDealsByCategory = (dealsList) => {
  const result = { food: [], drinks: [], other: [] };

  dealsList.forEach(deal => {
    const category = categorizeDeal(deal.fields['Deal Name'], deal.fields.Description);
    const parsedItems = parseItemsFromDescription(deal.fields.Description);

    if (parsedItems.length > 1) {
      // For multi-item deals, always split by individual item category regardless of deal-level label.
      // This handles cases where the deal name is drink-focused (e.g. "Beer & Wine Special") but
      // the description also contains food items.
      const foodItems  = parsedItems.filter(i => i.category === 'Food');
      const drinkItems = parsedItems.filter(i => i.category === 'Drinks');
      const otherItems = parsedItems.filter(i => i.category === 'Other');
      const isMixed = drinkItems.length > 0 && (foodItems.length > 0 || otherItems.length > 0);

      if (isMixed) {
        if (drinkItems.length > 0) result.drinks.push({ ...deal, parsedItems: drinkItems, originalCategory: category });
        // Merge food + unrecognized items into one food entry (no visual gap between them)
        const nonDrinkItems = [...foodItems, ...otherItems];
        if (nonDrinkItems.length > 0) result.food.push({ ...deal, parsedItems: nonDrinkItems, originalCategory: category });
      } else {
        // All items same type — use deal-level category for bucket placement
        const parsedWithCategory = { ...deal, parsedItems };
        if (category === 'Food' || otherItems.length === parsedItems.length) result.food.push(parsedWithCategory);
        else if (category === 'Drinks') result.drinks.push(parsedWithCategory);
        else result.food.push(parsedWithCategory);
      }
    } else {
      // Single or zero parsed items — use deal-level category (more reliable for single cocktails)
      const parsedWithCategory = parsedItems.length > 0 ? { ...deal, parsedItems } : deal;
      if (category === 'Food') result.food.push(parsedWithCategory);
      else if (category === 'Drinks') result.drinks.push(parsedWithCategory);
      else result.food.push(parsedWithCategory);
    }
  });

  return result;
};

// ============================================================================
// FORMATTING LOGIC
// ============================================================================

export const formatDescriptionLogic = (description) => {
  if (!description) return null;

  const normalized = description.replace(/\n/g, ' ').replace(/([^\s(])\$/g, '$1 $');
  const hasPricing = /\$\d+/.test(normalized);

  if (!hasPricing) return { type: 'plain', text: description };

  const pricePattern = /(\$\d+(?:\.\d{2})?)\s+([^$]+?)(?=\$\d+|$)/g;
  const matches = [...normalized.matchAll(pricePattern)];
  const matchedTextLength = matches.reduce((sum, m) => sum + m[0].length, 0);
  const coverageRatio = matchedTextLength / description.length;

  if (matches.length < 2 || coverageRatio < 0.5) return { type: 'plain', text: description };

  return {
    type: 'structured',
    items: matches.map(m => ({ price: m[1], items: m[2].trim() }))
  };
};

/**
 * Format a description into JSX.
 */
export const formatDescription = (description) => {
  const result = formatDescriptionLogic(description);
  if (!result) return null;

  if (result.type === 'plain') {
    return <p>{result.text}</p>;
  }

  return (
    <div className="deal-pricing">
      {result.items.map((item, index) => (
        <div key={index} className="price-item">
          <span className="price">{item.price}</span>
          <span className="items">{item.items}</span>
        </div>
      ))}
    </div>
  );
};

// ============================================================================
// PRICE EXTRACTION
// ============================================================================

export const extractPrice = (description) => {
  if (!description) return null;
  const priceMatch = description.match(/\$\d+(?:\.\d{2})?/);
  return priceMatch ? priceMatch[0] : null;
};

// ============================================================================
// DAY FORMATTING
// ============================================================================

export const formatDaysLabel = (days) => {
  if (!days || days.length === 0) return 'Daily';
  if (days.length === 1 && days[0].toLowerCase() === 'everyday') return 'Daily';

  const dayOrder = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
  const sortedDays = [...days].sort((a, b) => dayOrder.indexOf(a) - dayOrder.indexOf(b));

  const weekdays = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'];
  const weekends = ['Saturday', 'Sunday'];
  const allDays = [...weekdays, ...weekends];

  const isWeekdays = weekdays.every(d => sortedDays.includes(d)) && sortedDays.length === 5;
  const isWeekends = weekends.every(d => sortedDays.includes(d)) && sortedDays.length === 2;
  const isDaily = allDays.every(d => sortedDays.includes(d));

  if (isDaily) return 'Daily';
  if (isWeekdays) return 'Weekdays';
  if (isWeekends) return 'Weekends';

  if (sortedDays.length === 1) return sortedDays[0] + 's';

  const abbrev = {
    'Monday': 'Mon', 'Tuesday': 'Tue', 'Wednesday': 'Wed',
    'Thursday': 'Thu', 'Friday': 'Fri', 'Saturday': 'Sat', 'Sunday': 'Sun'
  };
  return sortedDays.map(d => abbrev[d] || d).join(', ');
};
